/**
 * Formatting Utilities
 * Reusable functions for consistent data formatting across the application
 */

import type { UserProfile } from "../types";

/**
 * Format currency with symbol and rate suffix
 * @param amount - The amount to format
 * @param currency - The currency symbol (default: €)
 * @param suffix - Optional suffix (default: /hour)
 */
export const formatCurrency = (
  amount: number,
  currency: string = "€",
  suffix: string = "/hour",
): string => {
  return `${currency} ${amount} ${suffix}`;
};

/**
 * Format a date range as a readable string
 * @param start - Start date
 * @param end - End date
 * @param locale - Locale for formatting (default: en-IE)
 */
export const formatDateRange = (
  start: Date,
  end: Date,
  locale: string = "en-IE",
  timeZone?: string,
): string => {
  const options: Intl.DateTimeFormatOptions = {
    dateStyle: "short",
    timeStyle: "short",
    ...(timeZone && { timeZone }),
  };

  return `${start.toLocaleString(locale, options)} - ${end.toLocaleString(locale, options)}`;
};

/**
 * Format a single date/time
 * @param date - Date to format
 * @param locale - Locale for formatting (default: en-IE)
 * @param timeZone - IANA timezone string (e.g. "Europe/Dublin")
 */
export const formatDateTime = (
  date: Date,
  locale: string = "en-IE",
  timeZone?: string,
): string => {
  return date.toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "short",
    ...(timeZone && { timeZone }),
  });
};

/**
 * Format user's full name
 * @param user - User profile object
 * @returns Formatted full name or 'Unknown' if user is undefined
 */
export const formatUserName = (user: UserProfile | undefined): string => {
  if (!user) return "Unknown";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
};

/**
 * Format user's full name from first and last name strings
 * @param firstName - First name
 * @param lastName - Last name
 */
export const formatName = (
  firstName: string | undefined,
  lastName: string | undefined,
): string => {
  return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
};

/**
 * Format location with emoji
 * @param address - Location address
 */
export const formatLocation = (address: string): string => {
  return address;
};

/**
 * Truncate text to a maximum length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Find and format user name from a list of users by ID
 * @param userId - The user ID to look up
 * @param users - Array of user profiles to search
 * @param fallback - Fallback text if user not found (default: 'Unknown')
 */
export const getUserNameById = (
  userId: string | undefined,
  users: UserProfile[],
  fallback: string = "Unknown",
): string => {
  if (!userId) return fallback;
  const user = users.find((u) => u.uid === userId);
  return user ? formatUserName(user) : fallback;
};

/**
 * Day names for schedule formatting
 */
const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Format recurrence pattern for display
 * @param recurrenceFrequency - The frequency type
 * @param daysOfWeek - Array of day numbers (0=Sunday)
 * @param startTime - Start time string (HH:mm)
 * @param endTime - End time string (HH:mm)
 */
export const formatRecurrence = (
  recurrenceFrequency: string,
  daysOfWeek: number[],
  startTime: string,
  endTime: string,
): string => {
  const days = daysOfWeek.map((d) => DAY_NAMES_SHORT[d]).join(", ");

  let freqText = "";
  switch (recurrenceFrequency) {
    case "daily":
      freqText = "Daily";
      break;
    case "weekly":
      freqText = "Weekly";
      break;
    case "biweekly":
      freqText = "Every 2 weeks";
      break;
    case "monthly":
      freqText = "Monthly";
      break;
    default:
      freqText = recurrenceFrequency || "Recurring";
  }

  return `${freqText} on ${days}, ${startTime} - ${endTime}`;
};
